import { useCallback } from "react";
import _ from "lodash";

import Plan from "types/Plan";
import { useAllPlans } from "routes/data-routes/billing-data/plans/useAllPlans";
import { getMonthlyEquivalent, hasToContactUsBeforeChange, isLegacyAnnualPlan, isYearlyPlan } from "utils/planUtils";


type PlanChangeKind = "invalid" | "same" | "contact-sales" | "upgrade" | "downgrade";

type PlanHelpersType = {
  /**
   * Returns the previous plan to downgrade to, or undefined if there is no previous plan.
   * For annual plans, it will offer the previous monthly plan (if any).
   */
  getDowngradeTarget: (planSlug: string | undefined) => Plan | undefined;
  /**
   * Returns the next plan to upgrade to, or undefined if there is no next plan.
   * For annual plans, it will offer the next monthly plan (if any).
   */
  getUpgradeTarget: (planSlug: string | undefined) => Plan | undefined;
  getPlanChangeKind: (planSlug: string | undefined, targetPlanSlug: string | undefined) => PlanChangeKind | undefined;
  getPlan: (planSlug: string | undefined) => Plan | undefined;
};


export function usePlanHelpers(): PlanHelpersType {
  const { allPlans } = useAllPlans();

  const getPlanIdx = useCallback(
    (planSlug: string | undefined): number => {
      if (planSlug && allPlans) {
        return allPlans.findIndex(planGroup => planGroup.some(plan => plan.planSlug === planSlug));
      }
      return -1;
    },
    [ allPlans ]
  );

  const getDowngradeTarget = useCallback<PlanHelpersType["getDowngradeTarget"]>(
    (planSlug) => {
      if (allPlans && planSlug) {
        const searchPlanSlug = getMonthlyEquivalent(planSlug);
        const currentIdx = getPlanIdx(searchPlanSlug);
        if (currentIdx >= 0) {
          return _.findLast(allPlans, planGroup => (planGroup.length > 0) && (planGroup[0].category !== undefined), currentIdx - 1)?.[0];
        }
      }
    },
    [ allPlans, getPlanIdx ]
  );

  const getPlan = useCallback<PlanHelpersType["getPlan"]>(
    (planSlug) => {
      if (planSlug && allPlans) {
        return _.find(allPlans.flat(), plan => plan.planSlug === planSlug);
      }
    },
    [ allPlans ]
  );

  const getPlanChangeKind = useCallback<PlanHelpersType["getPlanChangeKind"]>(
    (planSlug, targetPlanSlug) => {
      if (!allPlans) {
        // plans data is not loaded yet, cannot determine the change kind
        return undefined;
      }

      if (planSlug && targetPlanSlug) {
        if (planSlug === targetPlanSlug) {
          return "same";
        }

        if (hasToContactUsBeforeChange(planSlug) || hasToContactUsBeforeChange(targetPlanSlug)) {
          return "contact-sales";
        }

        // plan change kind is purely based on MRR.
        // the backend systems return the plans in ascending MRR order, so this check here is simply
        // a comparison of positions.
        const currentIdx = getPlanIdx(planSlug);
        const targetIdx = getPlanIdx(targetPlanSlug);

        if (currentIdx === targetIdx) {
          // UI exception to our "MMR-based" rules, where:
          // * moving to same or higher MRR -> immediate upgrade/renewal
          // * moving to lower MRR -> scheduled downgrade
          // since based on MRR moving from monthly to the matching annual plan would be `same`, we have to add
          // another layer here, so monthly->annual would be shown as an upgrade on the UI, and
          // annual->monthly would be shown as a downgrade (regardless of they are both immediate actions in the
          // backend services)
          const isCurrentYearlyOrAnnual = isYearlyPlan(planSlug) || isLegacyAnnualPlan(targetPlanSlug);
          const isTargetYearlyOrAnnual = isYearlyPlan(targetPlanSlug) || isLegacyAnnualPlan(targetPlanSlug);
          if (isCurrentYearlyOrAnnual && !isTargetYearlyOrAnnual) {
            return "downgrade";
          } else if (!isCurrentYearlyOrAnnual && isTargetYearlyOrAnnual) {
            return "upgrade";
          } else {
            return "same";
          }
        } else if (currentIdx < targetIdx) {
          return "upgrade";
        } else {
          return "downgrade";
        }
      }

      return "invalid";
    },
    [ allPlans, getPlanIdx ]
  );

  const getUpgradeTarget = useCallback<PlanHelpersType["getUpgradeTarget"]>(
    (planSlug) => {
      if (allPlans && planSlug) {
        const searchPlanSlug = getMonthlyEquivalent(planSlug);
        const currentIdx = getPlanIdx(searchPlanSlug);
        if (currentIdx >= 0) {
          return _.find(allPlans, planGroup => (planGroup.length > 0) && (planGroup[0].category !== undefined) && !isYearlyPlan(planGroup[0].planSlug) && !isLegacyAnnualPlan(planGroup[0].planSlug), currentIdx + 1)?.[0];
        }
      }
    },
    [ allPlans, getPlanIdx ]
  );

  return {
    getDowngradeTarget,
    getPlan,
    getPlanChangeKind,
    getUpgradeTarget
  };
}
